import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, NavLink, Routes, Navigate } from 'react-router-dom';
import { useNProgressLoader } from './hooks/useNProgressLoader';
import './App.css';
import EmployeesList from './components/EmployeesList';
import AssetsList from './components/AssetsList';
import AccountabilityHeader from './components/AccountabilityHeader';
import EmployeeDetails from './components/EmployeeDetails';
import TimeDetails from './components/TimeDetails';
import IncidentsCRUD from './components/IncidentsCRUD';
import TimeTrackList from './components/TimeTrackList';
import SpaceTrackList from './components/SpaceTrackList';
import SpaceDetails from './components/SpaceDetails';
import MonitorList from './components/MonitorList';
import MonitorDetail from './components/MonitorDetail';
import CostsList from './components/CostsList';
import UsersCRUD from './components/UsersCRUD';
import 'nprogress/nprogress.css';

import { fetchAuthSession } from 'aws-amplify/auth';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';

// Helper function to set the document title
const useDocumentTitle = (title) => {
  useEffect(() => {
    document.title = title;
  }, [title]);
};

// Inline stroke icons so the collapsed rail stays readable without labels.
// currentColor means they inherit hover/active state for free — no icon library.
const Icon = ({ d }) => (
  <svg className="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
    {d.map((path, i) => <path key={i} d={path} />)}
  </svg>
);

const NAV_ITEMS = [
  { to: '/costs',      label: 'Costs Analysis',      d: ['M12 1v22', 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'] },
  { to: '/monitors',   label: 'Developer Activity',  d: ['M3 12h4l2 6 4-14 2 8h6'] },
  { to: '/times',      label: 'Time Tracking',       d: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M12 7v5l3 2'] },
  { to: '/spaces',     label: 'Time by Project',     d: ['M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z'] },
  // Severity Scale was removed from here and folded into Incident Types. It listed the
  // same five categories that page groups by, so as its own route it was a legend on a
  // separate sheet from the thing it explained. /categories now redirects there.
  { to: '/incidents',  label: 'Incident Types',      d: ['M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z', 'M12 9v4', 'M12 17h.01'] },
];

// Appended to the nav only for admins. Hiding it is presentation, not security —
// usersHandler re-checks group membership on every request, because a token
// claim can be edited in the browser.
const ADMIN_NAV_ITEM = {
  to: '/users',
  label: 'Users',
  d: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
};

// Reads the group claim from the ID token once per mount.
const useIsAdmin = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let live = true;
    fetchAuthSession()
      .then((session) => {
        const claims = session.tokens?.idToken?.payload ?? {};
        if (live) setIsAdmin((claims['cognito:groups'] || []).includes('admins'));
      })
      .catch(() => { if (live) setIsAdmin(false); });
    return () => { live = false; };
  }, []);
  return isAdmin;
};

const components = {
  Header() {
    useDocumentTitle("Accountability - AI Wehaa");
    return (
      // Served from public/ as a transparent PNG. This used to be a JPEG hotlinked
      // from a third-party newspaper CDN, so the first screen of an internal tool
      // depended on someone else's server — and JPEG can't hold transparency,
      // which is where the white box came from.
      <div className="auth-header">
        <img className="auth-logo" alt="Wehaa" src={`${process.env.PUBLIC_URL}/wehaa-logo.png`} />
        <p className="auth-tagline">Accountability</p>
      </div>
    );
  },

  Footer() {
    return (
      <div className="auth-footer">
        <p>&copy; {new Date().getFullYear()} Wehaa. All rights reserved.</p>
      </div>
    );
  },

  // Each screen below used to share the heading "Enter Information:" and a
  // footer that literally read "Footer Information" — placeholder text that
  // shipped to production. Each now says what the screen actually wants.
  SignIn: {
    Header() {
      return (
        <div className="auth-step">
          <h2 className="auth-step-title">Sign in</h2>
          <p className="auth-step-hint">Use your Wehaa username, not your email address.</p>
        </div>
      );
    },
    Footer() {
      const { toForgotPassword } = useAuthenticator();

      return (
        <div className="auth-step-footer">
          <button type="button" className="auth-link" onClick={toForgotPassword}>
            Forgot your password?
          </button>
        </div>
      );
    },
  },
  ConfirmSignIn: {
    Header() {
      return (
        <div className="auth-step">
          <h2 className="auth-step-title">Confirm sign in</h2>
          <p className="auth-step-hint">Enter the verification code we sent you.</p>
        </div>
      );
    },
  },
  ForgotPassword: {
    Header() {
      return (
        <div className="auth-step">
          <h2 className="auth-step-title">Reset your password</h2>
          <p className="auth-step-hint">
            Enter your username and we'll email a reset code to the address on the account.
          </p>
        </div>
      );
    },
  },
  ConfirmResetPassword: {
    Header() {
      return (
        <div className="auth-step">
          <h2 className="auth-step-title">Choose a new password</h2>
          <p className="auth-step-hint">
            Enter the code from your email, then set a password of at least 8 characters.
          </p>
        </div>
      );
    },
  },
};

// Invite emails link here as /?username=<name> so the sign-in form arrives
// prefilled — the person only types the temporary password. Read once at module
// load: the Authenticator mounts before any routing, and the param never
// changes within a page load.
const invitedUsername = new URLSearchParams(window.location.search).get('username') || '';
const authFormFields = invitedUsername
  ? { signIn: { username: { defaultValue: invitedUsername } } }
  : undefined;

function App() {
  // Initialize the global loading handler
  useNProgressLoader();

  const isAdmin = useIsAdmin();
  const navItems = isAdmin ? [...NAV_ITEMS, ADMIN_NAV_ITEM] : NAV_ITEMS;

  // Persisted so the choice survives navigation and reloads.
  const [navCollapsed, setNavCollapsed] = useState(
    () => localStorage.getItem('navCollapsed') === '1'
  );

  const toggleNav = () => {
    setNavCollapsed((collapsed) => {
      localStorage.setItem('navCollapsed', collapsed ? '0' : '1');
      return !collapsed;
    });
  };

  return (
    <Authenticator hideSignUp components={components} formFields={authFormFields}>
      {({ signOut, user }) => (
        <Router>
          <div className={`App${navCollapsed ? ' nav-collapsed' : ''}`}>
            <div className="sidebar">
              <div className="sidebar-title">
                {/* Was the text "AI Wehaa", which is not the product's name. */}
                <img
                  className="sidebar-logo"
                  src={`${process.env.PUBLIC_URL}/wehaa-logo.png`}
                  alt="Wehaa"
                />
                <button
                  type="button"
                  className="nav-toggle"
                  onClick={toggleNav}
                  aria-expanded={!navCollapsed}
                  aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                  title={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                >
                  <svg className="nav-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3 6h18" /><path d="M3 12h18" /><path d="M3 18h18" />
                  </svg>
                </button>
              </div>
              <nav>
                {/* .active styling lives in App.css — inline styles used to win over it */}
                <ul>
                  {navItems.map(({ to, end, label, d }) => (
                    <li key={to}>
                      {/* title= keeps the label reachable when collapsed to icons */}
                      <NavLink to={to} end={end} title={label}>
                        <Icon d={d} />
                        <span className="nav-label">{label}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="sidebar-footer">
                {/* `user` was destructured but never rendered — the sidebar gave no
                    indication of who was signed in. */}
                <div className="account">
                  <span className="account-avatar" aria-hidden="true">
                    {(user?.username || '?').slice(0, 2).toUpperCase()}
                  </span>
                  <span className="account-detail">
                    <span className="account-name">{user?.username || 'Signed in'}</span>
                    <span className="account-role">Signed in</span>
                  </span>
                </div>
                {/* was a <span onClick> — not focusable, so keyboard users could not sign out */}
                <button
                  type="button"
                  className="signout-button"
                  onClick={signOut}
                  title="Sign out"
                  aria-label="Sign out"
                >
                  <svg className="signout-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                  </svg>
                  {/* wrapped so the collapsed rail can hide it — as a bare text
                      node it survived display:none and wrapped onto two lines */}
                  <span className="nav-label">Sign out</span>
                </button>
              </div>
            </div>
            <div className="content">
              <header className="App-header">
                <AccountabilityHeader />
              </header>
              <main className="page">
              <Routes>
                <Route
                  path="/"
                  element={<EmployeesListWithTitle />}
                />
                <Route
                  path="/assets"
                  element={<AssetsListWithTitle />}
                />
                <Route
                  path="/monitors"
                  element={<MonitorListWithTitle />}
                />
                <Route 
                  path="/times" 
                  element={<TimeTrackListWithTitle />} 
                />
                <Route 
                  path="/spaces" 
                  element={<SpaceTrackListWithTitle />} 
                />
                <Route 
                  path="/incidents" 
                  element={<IncidentsCRUDWithTitle />} 
                />
                {/* Kept as a redirect rather than deleted: this route was in the nav for
                    years, so it is bookmarked and linked. replace so it does not sit in
                    history and trap the back button. */}
                <Route
                  path="/categories"
                  element={<Navigate to="/incidents" replace />}
                />
                <Route 
                  path="/employee/:id" 
                  element={<EmployeeDetailsWithTitle />} 
                />
                <Route 
                  path="/monitors/:user_id" 
                  element={<MonitorDetailWithTitle />} 
                />
                <Route 
                  path="/times/:id" 
                  element={<TimeDetailsWithTitle />} 
                />
                <Route 
                  path="/spaces/:id" 
                  element={<SpaceDetailsWithTitle />} 
                />
                <Route
                  path="/costs"
                  element={<CostsListWithTitle />}
                />
                {/* Always routed, never conditionally — UsersCRUD renders its own
                    "limited to the admins group" message, so a non-admin who types
                    the URL gets an explanation rather than a blank page. */}
                <Route
                  path="/users"
                  element={<UsersCRUDWithTitle />}
                />
              </Routes>
              </main>
            </div>
          </div>
        </Router>
      )}
    </Authenticator>
  );
}

// Wrap each component to set the document title

const CostsListWithTitle = () => {
  useDocumentTitle("Costs Analysis - AI Wehaa");
  return <CostsList />;
};


const EmployeesListWithTitle = () => {
  useDocumentTitle("Liability Ranking - AI Wehaa");
  return <EmployeesList />;
};

const AssetsListWithTitle = () => {
  useDocumentTitle("Asset Ranking - AI Wehaa");
  return <AssetsList />;
};

const MonitorListWithTitle = () => {
  useDocumentTitle("Developer Activity - AI Wehaa");
  return <MonitorList />;
};

const MonitorDetailWithTitle = () => {
  useDocumentTitle("Workload Detail - AI Wehaa");
  return <MonitorDetail />;
};

const TimeTrackListWithTitle = () => {
  useDocumentTitle("Time Tracking - AI Wehaa");
  return <TimeTrackList />;
};

const SpaceTrackListWithTitle = () => {
  useDocumentTitle("Time on Spaces - AI Wehaa");
  return <SpaceTrackList />;
};

const IncidentsCRUDWithTitle = () => {
  useDocumentTitle("Incidents - AI Wehaa");
  return <IncidentsCRUD />;
};

const UsersCRUDWithTitle = () => {
  useDocumentTitle("Users - AI Wehaa");
  return <UsersCRUD />;
};

const EmployeeDetailsWithTitle = () => {
  useDocumentTitle("Employee Details - AI Wehaa");
  return <EmployeeDetails />;
};

const TimeDetailsWithTitle = () => {
  useDocumentTitle("Time Details - AI Wehaa");
  return <TimeDetails />;
};

const SpaceDetailsWithTitle = () => {
  useDocumentTitle("Space Details - AI Wehaa");
  return <SpaceDetails />;
};

export default App;
